import { DeviceWithInstalledPower, ForecastAssistantButton } from '@e2m-customer-portal/domains';
import { convertMeasure, InputFieldType } from '@e2m-customer-portal/shared';
import { DowntimeButton, ExpandIcon } from '@e2m-customer-portal/ui';
import { Button } from '@mui/material';
import clsx from 'clsx';
import { Interval } from 'date-fns';
import { MarketLocation } from 'generated/graphql';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface ControlDeviceTableHeaderProps {
  open?: boolean;
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  interval: Interval;
  products: Array<InputFieldType>;
  selectedProducts: Set<InputFieldType>;
  disableAssistant: boolean;
  disabled?: boolean;
  isReadOnlyBattery: boolean;
  onClick?: () => void;
  setSelectedProducts: React.Dispatch<React.SetStateAction<Set<InputFieldType>>>;
  isOptimizedBattery?: boolean;
}

export function ControlDeviceTableHeader({
  open,
  marketLocation,
  device,
  interval,
  products,
  selectedProducts,
  disableAssistant,
  disabled,
  isReadOnlyBattery,
  onClick,
  setSelectedProducts,
  isOptimizedBattery,
}: Readonly<ControlDeviceTableHeaderProps>): ReactElement {
  const [t] = useTranslation();
  const installedPower = device.installedPower && convertMeasure(device.installedPower, 'kW');
  return (
    <div
      className={clsx(
        'device-table-header flex flex-wrap items-center border-0 border-b border-solid border-divider bg-white py-2 text-left sm:justify-end xl:flex-nowrap',
        disabled ? 'text-grey-300' : 'text-black',
      )}
      aria-expanded={open}
      data-testid={`${device.id}/${device.name}`}
    >
      <div className="ml-6 flex w-fit grow flex-col xl:w-full">
        <div className="flex flex-row items-center gap-4">
          <div className="ml-2 flex flex-col gap-1 whitespace-nowrap">
            <span className="text-xs">
              {device.externalName ? device.name : t('DEVICE_TABLE_HEADER_TECHNICAL_UNIT')}
            </span>
            <span className="flex items-center sm:text-xl">{device.externalName || device.name}</span>
          </div>
          <Button
            className="bg-grey-50"
            name={device.name}
            endIcon={<ExpandIcon className={clsx('mr-3 leading-none', { 'fill-grey-300': disabled })} open={open} />}
            onClick={onClick}
          />
          {installedPower && (
            <div className="flex w-max items-center border-0 border-l border-divider border-solid">
              <div className="ml-2 flex flex-col gap-1 whitespace-nowrap">
                <span className="text-xs">{t('DEVICE_TABLE_HEADER_INSTALLED_POWER_LABEL')}</span>
                <div className="sm:text-xl">
                  <div className="flex items-center">
                    {t('DEVICE_TABLE_HEADER_INSTALLED_POWER', { value: installedPower.value })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mx-4 mt-2 flex w-full flex-wrap items-center justify-end gap-2 border-0 border-t border-solid border-t-divider pt-2 md:flex-nowrap xl:my-0 xl:w-auto xl:border-none xl:pt-0 2xl:shrink-0">
        {products.length > 1 && (
          <div className="mx-2 sm:mx-4">
            <div
              data-testid={`device-table-product-filter-${device.id}`}
              className="flex overflow-hidden rounded-lg border border-solid border-divider text-sm"
            >
              <label
                className={clsx(
                  'relative w-max from-black/5 to-black/5 px-2 font-bold leading-6 transition-colors hover:bg-gradient-to-r',
                  {
                    'bg-green-200 text-white': selectedProducts.size === products.length,
                  },
                )}
              >
                {t('SHARED_ALL')}
                <input
                  type="checkbox"
                  className="absolute inset-0 m-0 block appearance-none"
                  checked={selectedProducts.size === products.length}
                  onChange={() => {}}
                  onClick={(event) => {
                    setSelectedProducts((selectedProducts) => {
                      if (selectedProducts.size === products.length) {
                        return new Set();
                      } else {
                        return new Set(products);
                      }
                    });
                    event.stopPropagation();
                  }}
                />
              </label>
              {products.map((product) => (
                <label
                  key={product}
                  className={clsx(
                    'relative w-max border-0 border-l border-solid border-divider from-black/5 to-black/5 px-2 leading-6 transition-colors hover:bg-gradient-to-r',
                    {
                      'bg-green-200 text-white': selectedProducts.has(product),
                    },
                  )}
                >
                  {t(`SHARED_PRODUCT_${product}`)}
                  <input
                    type="checkbox"
                    className="absolute inset-0 m-0 block appearance-none"
                    checked={selectedProducts.has(product)}
                    onChange={() => {}}
                    onClick={(event) => {
                      setSelectedProducts((selectedProducts) => {
                        const toggledproducts = new Set([...selectedProducts]);
                        if (toggledproducts.has(product)) toggledproducts.delete(product);
                        else toggledproducts.add(product);
                        return toggledproducts;
                      });
                      event.stopPropagation();
                    }}
                  />
                </label>
              ))}
            </div>
          </div>
        )}
        {!isReadOnlyBattery && !isOptimizedBattery && (
          <>
            <ForecastAssistantButton
              marketLocation={marketLocation}
              device={device}
              disabled={!marketLocation.permissions.editForecast || disableAssistant}
              interval={interval}
              label={'FORECAST_ASSISTANT_CONTROL_ENERGY_BUTTON_LABEL' as unknown as TemplateStringsArray}
            />
            <DowntimeButton
              marketLocation={marketLocation}
              device={device}
              disabled={!marketLocation.permissions.editForecast || disableAssistant}
            />
          </>
        )}
      </div>
    </div>
  );
}
